<template>
  <div
    class="base-pagination"
    :class="{ 'is-rounded': rounded }"
  >
    <div
      v-if="showInfo"
      class="pagination-info"
    >
      <span>{{ $t('general.showing') }} {{ startItem }}-{{ endItem }} {{ $t('general.of') }} {{ totalItems }}</span>
    </div>

    <div class="pagination-controls">
      <!-- Previous button -->
      <component
        :is="pageLink && currentPage > 1 ? 'a' : 'button'"
        v-if="!simple"
        :href="pageLink && currentPage > 1 ? pageLink(currentPage - 1) : undefined"
        class="pagination-btn pagination-prev"
        :class="{ 'is-disabled': currentPage <= 1 }"
        :disabled="currentPage <= 1"
        :aria-label="$t('general.previous')"
        @click="navigatePage($event, currentPage - 1)"
      >
        <span class="icon">
          <i class="fas fa-chevron-left" />
        </span>
      </component>

      <!-- First page button -->
      <component
        :is="pageLink ? 'a' : 'button'"
        v-if="showFirstLast && !visiblePageNumbers.includes(1) && !simple"
        :href="pageLink ? pageLink(1) : undefined"
        class="pagination-btn pagination-number"
        @click="navigatePage($event, 1)"
      >
        1
      </component>

      <!-- Start ellipsis -->
      <div
        v-if="startEllipsis && !simple"
        class="pagination-ellipsis"
      >
        <i class="fas fa-ellipsis-h" />
      </div>

      <!-- Page numbers -->
      <div
        v-if="!simple"
        class="pagination-numbers"
      >
        <component
          :is="pageLink ? 'a' : 'button'"
          v-for="page in visiblePageNumbers"
          :key="page"
          :href="pageLink ? pageLink(page) : undefined"
          class="pagination-btn pagination-number"
          :class="{ 'is-active': page === currentPage }"
          :aria-current="page === currentPage ? 'page' : undefined"
          @click="navigatePage($event, page)"
        >
          {{ page }}
        </component>
      </div>

      <!-- Current page info for simple mode -->
      <div
        v-if="simple"
        class="pagination-simple"
      >
        <span>{{ currentPage }}</span>
        <span class="pagination-simple-divider">/</span>
        <span>{{ totalPages }}</span>
      </div>

      <!-- End ellipsis -->
      <div
        v-if="endEllipsis && !simple"
        class="pagination-ellipsis"
      >
        <i class="fas fa-ellipsis-h" />
      </div>

      <!-- Last page button -->
      <component
        :is="pageLink ? 'a' : 'button'"
        v-if="showFirstLast && !visiblePageNumbers.includes(totalPages) && !simple"
        :href="pageLink ? pageLink(totalPages) : undefined"
        class="pagination-btn pagination-number"
        @click="navigatePage($event, totalPages)"
      >
        {{ totalPages }}
      </component>

      <!-- Next button -->
      <component
        :is="pageLink && currentPage < totalPages ? 'a' : 'button'"
        :href="pageLink && currentPage < totalPages ? pageLink(currentPage + 1) : undefined"
        class="pagination-btn pagination-next"
        :class="{ 'is-disabled': currentPage >= totalPages }"
        :disabled="currentPage >= totalPages"
        :aria-label="$t('general.next')"
        @click="navigatePage($event, currentPage + 1)"
      >
        <span class="icon">
          <i class="fas fa-chevron-right" />
        </span>
      </component>
    </div>

    <!-- Per page selector -->
    <div
      v-if="showPerPage"
      class="pagination-per-page"
    >
      <span>{{ $t('general.per-page') }}:</span>
      <div class="select is-small">
        <select
          v-model="perPageValue"
          @change="updatePerPage"
        >
          <option
            v-for="option in perPageOptions"
            :key="option"
            :value="option"
          >
            {{ option }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  pageLink: { type: Function, default: null },
  currentPage: {
    type: Number,
    required: true,
  },
  totalPages: {
    type: Number,
    required: true,
  },
  totalItems: {
    type: Number,
    default: 0,
  },
  perPage: {
    type: Number,
    default: 10,
  },
  showPerPage: {
    type: Boolean,
    default: false,
  },
  perPageOptions: {
    type: Array,
    default: () => [10, 25, 50, 100],
  },
  simple: {
    type: Boolean,
    default: false,
  },
  rounded: {
    type: Boolean,
    default: true,
  },
  showFirstLast: {
    type: Boolean,
    default: true,
  },
  showInfo: {
    type: Boolean,
    default: false,
  },
  maxVisible: {
    type: Number,
    default: 5,
  },
  showEdges: {
    type: Number,
    default: 2,
  },
});

const emit = defineEmits(['page-change', 'per-page-change']);

const perPageValue = ref(props.perPage);

// Keep ordinary pagination events, while allowing link opening/copying and
// modified clicks to retain native browser behavior for public collections.
function navigatePage(event, page) {
  if (props.pageLink && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)) return;
  event.preventDefault();
  changePage(page);
}

// Computed properties
const startItem = computed(() => {
  return props.totalItems === 0 ? 0 : (props.currentPage - 1) * props.perPage + 1;
});

const endItem = computed(() => {
  const end = props.currentPage * props.perPage;
  return end > props.totalItems ? props.totalItems : end;
});

const visiblePageNumbers = computed(() => {
  if (props.totalPages <= props.maxVisible) {
    return Array.from({ length: props.totalPages }, (_, i) => i + 1);
  }

  const halfVisible = Math.floor(props.maxVisible / 2);
  let start = props.currentPage - halfVisible;
  let end = props.currentPage + halfVisible;

  if (start < 1) {
    end = Math.min(props.totalPages, end + (1 - start));
    start = 1;
  }

  if (end > props.totalPages) {
    start = Math.max(1, start - (end - props.totalPages));
    end = props.totalPages;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const startEllipsis = computed(() => {
  return !props.simple && props.totalPages > props.maxVisible && visiblePageNumbers.value[0] > 1 + props.showEdges;
});

const endEllipsis = computed(() => {
  return !props.simple && props.totalPages > props.maxVisible &&
    visiblePageNumbers.value[visiblePageNumbers.value.length - 1] < props.totalPages - props.showEdges;
});

// Methods
const changePage = (page) => {
  if (page < 1 || page > props.totalPages || page === props.currentPage) return;
  emit('page-change', page);
};

const updatePerPage = () => {
  emit('per-page-change', perPageValue.value);
};

// Watch for prop changes
watch(() => props.perPage, (newVal) => {
  perPageValue.value = newVal;
});
</script>

<style lang="scss" scoped>
@use '@/styles/abstracts' as a;

.base-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  font-size: 0.95rem;

  @media (min-width: 768px) {
    justify-content: space-between;
  }

  .pagination-info {
    color: var(--color-text-light);
    font-size: 0.9rem;

    @media (max-width: 767px) {
      width: 100%;
      text-align: center;
      margin-bottom: 0.5rem;
    }
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .pagination-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.25rem;
    min-width: 2.25rem;
    padding: 0 0.75rem;
    background-color: var(--color-card-background);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom,
        rgba(255, 255, 255, 0.05),
        rgba(0, 0, 0, 0.05)
      );
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:hover:not(.is-disabled):not(.is-active) {
      border-color: var(--color-primary);
      color: var(--color-primary);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      &::after {
        opacity: 1;
      }
    }

    &.is-active {
      background-color: var(--color-primary-light, lighten($indiefy-blue, 25%));
      color: white;
      border-color: var(--color-primary, $indiefy-blue);
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &.is-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    &.pagination-prev, &.pagination-next {
      font-size: 0.8rem;
    }
  }

  &.is-rounded {
    .pagination-btn {
      border-radius: 999px;

      &.pagination-prev {
        border-radius: 999px;
      }

      &.pagination-next {
        border-radius: 999px;
      }
    }
  }

  .pagination-numbers {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .pagination-ellipsis {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.25rem;
    min-width: 2.25rem;
    color: var(--color-text-light);
    font-size: 0.8rem;
  }

  .pagination-simple {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.25rem;
    padding: 0 0.5rem;
    background-color: var(--color-card-background);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    border-radius: 4px;

    .pagination-simple-divider {
      margin: 0 0.5rem;
      opacity: 0.5;
    }
  }

  .pagination-per-page {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--color-text-light);
    font-size: 0.9rem;

    @media (max-width: 767px) {
      width: 100%;
      justify-content: center;
      margin-top: 0.5rem;
    }

    .select {
      select {
        border-color: var(--color-border);
        background-color: var(--color-card-background);
        color: var(--color-text);
      }
    }
  }
}
</style>